import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { config } from '../config/env';

interface LocationContextType {
  lat: number;
  lng: number;
  country?: string;
  state?: string;
  city?: string;
  timezone?: string;
  utility?: string;
  tariffId?: string;
  postalCode?: string;
  address?: string;
  loading: boolean;
  error: string | null;
  updateLocation: (location: Partial<LocationContextType>) => Promise<void>;
  refreshLocation: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

interface LocationProviderProps {
  children: ReactNode;
}

export const LocationProvider: React.FC<LocationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [location, setLocation] = useState<Omit<LocationContextType, 'updateLocation' | 'refreshLocation' | 'loading' | 'error'>>({
    lat: config.defaultLocation.lat,
    lng: config.defaultLocation.lng,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserLocation = async () => {
    if (!user?.id) {
      // Try browser geolocation as fallback
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
            setLoading(false);
          },
          () => {
            setLoading(false);
          },
        );
      } else {
        setLoading(false);
      }
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${config.apiUrl}/api/location`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.location) {
        setLocation(response.data.location);
      } else {
        // Fallback to browser geolocation
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setLocation({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
            },
            () => {
              // Use default location
            },
          );
        }
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch user location:', err);
      // Fallback to browser geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          () => {
            // Use default location already set
          },
        );
      }
      setError(err.message || 'Failed to load location');
    } finally {
      setLoading(false);
    }
  };

  const updateLocation = async (newLocation: Partial<LocationContextType>) => {
    if (!user?.id) {
      // Update local state only
      setLocation((prev) => ({ ...prev, ...newLocation }));
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `${config.apiUrl}/api/location`,
        newLocation,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      await fetchUserLocation();
      setError(null);
    } catch (err: any) {
      console.error('Failed to update location:', err);
      setError(err.message || 'Failed to update location');
      throw err;
    }
  };

  const refreshLocation = async () => {
    setLoading(true);
    await fetchUserLocation();
  };

  useEffect(() => {
    fetchUserLocation();
  }, [user?.id]);

  return (
    <LocationContext.Provider
      value={{
        ...location,
        loading,
        error,
        updateLocation,
        refreshLocation,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

